/**
 * Open-redirect / XSS guard for `returnTo` values that round-trip through
 * Auth0's `state` query parameter.
 *
 * `state` is attacker-controlled once it leaves Auth0 (the embedded-login
 * flow uses `auth0-js` which stores it in plain text). Passing it directly
 * to `router.replace` can execute arbitrary scripts when the value is a
 * `javascript:` URI, or redirect users off-origin with a scheme-relative or
 * absolute URL.
 *
 * @see {@link https://auth0.com/docs/secure/attack-protection/redirect-attacks}
 */

/**
 * Returns the path+search+hash of `raw` **only when it resolves to the
 * current origin**; returns `fallback` (default `/`) for everything else.
 *
 * Blocked patterns include:
 * - `javascript:` / `data:` / any non-http scheme
 * - Absolute URLs (`https://evil.com`)
 * - Scheme-relative URLs (`//evil.com`)
 * - Backslash-relative URLs (`/\evil.com`) — browsers normalise these to `//`
 * - Empty strings and non-string values
 *
 * The implementation resolves the value against `window.location.origin` and
 * then compares origins, so even obfuscated inputs are normalised before the
 * check.
 */
export const getSafeReturnTo = (raw: unknown, fallback = "/"): string => {
  if (typeof raw !== "string" || !raw) return fallback;

  try {
    const base = window.location.origin;
    const url = new URL(raw, base);

    if (url.origin !== base) return fallback;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
};
